import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Briefcase, MailSearch, CalendarClock, FileBarChart2, BellRing,
  Sparkles, ArrowRight, CheckCircle2, Hash, MapPin, Building2, Zap,
} from 'lucide-react';

const features = [
  { icon: MailSearch, title: 'AI Inbox Scanning', desc: 'Connect your email and AI reads incoming purchase orders, pulling out the PO number, site, scope and trades automatically.' },
  { icon: CalendarClock, title: 'Booking Calendar', desc: 'On-site dates are detected from emails and dropped onto a shared calendar so nothing slips through.' },
  { icon: FileBarChart2, title: 'Next-Day Reports', desc: 'Every evening, get a report of tomorrow’s jobs and exactly which trades each one needs.' },
  { icon: BellRing, title: 'Supplier Reminders', desc: 'Auto-email suppliers two days before you’re due on site to confirm access and updates.' },
];

const steps = [
  { n: '01', title: 'Connect your inbox', desc: 'Securely link your email in seconds with an app password.' },
  { n: '02', title: 'AI reads your POs', desc: 'New purchase orders are parsed into structured jobs — instantly.' },
  { n: '03', title: 'Stay ahead on site', desc: 'Bookings, trades and reminders run themselves. You just show up.' },
];

export const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="h-screen overflow-y-auto bg-grid text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl brand-gradient flex items-center justify-center shadow-glow">
              <Briefcase className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight font-heading">My Project Agent</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="landing-signin">Sign in</Button>
            <Button onClick={() => navigate('/login')} data-testid="landing-cta-top">
              Start free trial
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 right-0 h-[32rem] w-[32rem] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute top-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-secondary/50 text-xs font-medium mb-6">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              AI-powered PO & job management for contractors
            </div>
            <h1 className="font-heading font-bold tracking-tight text-5xl sm:text-6xl leading-[1.05]">
              Every purchase order,<br />
              <span className="text-gradient">handled by AI.</span>
            </h1>
            <p className="text-lg text-muted-foreground mt-6 max-w-xl">
              My Project Agent reads the POs your suppliers and insurance builders send, books the dates,
              lines up the trades and chases updates — so your crew is always one step ahead.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Button size="lg" className="h-12 px-6 text-base" onClick={() => navigate('/login')} data-testid="landing-cta-hero">
                Start free trial <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-6 text-base" onClick={() => navigate('/login')}>
                Sign in
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> No card required</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Set up in minutes</span>
            </div>
          </div>

          {/* Floating product mock */}
          <div className="relative">
            <div className="surface p-5 animate-float">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="h-8 w-8 rounded-lg brand-gradient flex items-center justify-center"><MailSearch className="h-4 w-4 text-white" /></span>
                  <span className="text-sm font-semibold">PO Inbox</span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full brand-gradient text-white flex items-center gap-1"><Sparkles className="h-3 w-3" />AI</span>
                </div>
                <span className="text-xs text-emerald-400 flex items-center gap-1"><Zap className="h-3 w-3" /> 97% match</span>
              </div>
              <div className="rounded-xl border border-border p-4 bg-background/40">
                <p className="font-semibold text-sm">Emergency make safe & roof repair</p>
                <p className="text-xs text-muted-foreground mb-3">from po@allinsure.com</p>
                <div className="grid grid-cols-2 gap-y-2 text-xs">
                  <span className="flex items-center gap-1.5 text-muted-foreground"><Hash className="h-3.5 w-3.5" /> PO-77812</span>
                  <span className="flex items-center gap-1.5 text-muted-foreground"><Building2 className="h-3.5 w-3.5" /> AllInsure Builders</span>
                  <span className="flex items-center gap-1.5 text-muted-foreground col-span-2"><MapPin className="h-3.5 w-3.5" /> 45 Ocean View Rd, Gold Coast</span>
                  <span className="flex items-center gap-1.5 text-amber-400 col-span-2"><CalendarClock className="h-3.5 w-3.5" /> On-site Tue 5 Aug</span>
                </div>
                <div className="flex gap-1.5 mt-3">
                  {['Roofer', 'Plasterer', 'Painter'].map((t) => (
                    <span key={t} className="text-[10px] px-2 py-0.5 rounded-md border border-border">{t}</span>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <span className="text-xs px-3 py-1.5 rounded-lg border border-border text-muted-foreground">Dismiss</span>
                <span className="text-xs px-3 py-1.5 rounded-lg brand-gradient text-white font-medium">Import as Job</span>
              </div>
            </div>
            <div className="absolute -bottom-5 -right-4 surface px-4 py-3 hidden sm:flex items-center gap-3">
              <span className="h-8 w-8 rounded-lg bg-amber-500/15 flex items-center justify-center"><BellRing className="h-4 w-4 text-amber-400" /></span>
              <div className="text-xs">
                <p className="font-medium">Reminder sent</p>
                <p className="text-muted-foreground">Supplier update · 2 days out</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Your back office, on autopilot</h2>
          <p className="text-muted-foreground mt-3">Four AI workflows that turn a messy inbox into a running schedule.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="surface card-hover p-6">
                <div className="h-11 w-11 rounded-xl brand-gradient flex items-center justify-center shadow-glow mb-4">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="p-6">
              <span className="font-heading text-5xl font-bold text-gradient">{s.n}</span>
              <h3 className="font-semibold mt-4 text-lg">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-2">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="relative overflow-hidden rounded-3xl brand-gradient p-10 sm:p-14 text-center">
          <div className="absolute -top-16 -left-10 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-16 -right-10 h-64 w-64 rounded-full bg-cyan-200/20 blur-3xl" />
          <div className="relative">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white">Stop chasing paperwork. Start tracking jobs.</h2>
            <p className="text-white/85 mt-3 max-w-xl mx-auto">Join contractors who let AI handle the POs while they run the job.</p>
            <Button size="lg" className="h-12 px-8 mt-8 bg-white text-slate-900 hover:bg-white/90" onClick={() => navigate('/login')} data-testid="landing-cta-bottom">
              Get started free <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg brand-gradient flex items-center justify-center"><Briefcase className="h-4 w-4 text-white" /></div>
            <span className="font-semibold text-foreground">My Project Agent</span>
          </div>
          <p>© {new Date().getFullYear()} My Project Agent · Built for the field, powered by AI.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
